/**
 * App.jsx — Root component and router for Inbox.
 *
 * Handles authentication gating, role-based routing, and the
 * main application structure. Unauthenticated users see the login screen.
 * Authorised users are routed to their role-specific dashboard.
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import AuthProvider, { useAuth } from './auth/AuthProvider';
import AppShell from './components/layout/AppShell';
import Login from './views/Login';
import RoleChooser from './views/RoleChooser';
import AdviserDashboard from './views/AdviserDashboard';
import AssistantDashboard from './views/AssistantDashboard';
import NewBusinessDashboard from './views/NewBusinessDashboard';
import SubmitFile from './views/SubmitFile';
import SubmitPrepRequest from './views/SubmitPrepRequest';
import PrepRequestDetail from './views/PrepRequestDetail';
import ReferralDetail from './views/ReferralDetail';
import FileDetail from './views/FileDetail';
import Settings from './views/Settings';
import Tools from './views/Tools';
import Reviews from './views/Reviews';
import Achievements from './views/Achievements';
import Reports from './views/Reports';
import IntroVideo from './views/IntroVideo';
import useCalendarPolling from './hooks/useCalendarPolling';
import useBackgroundScheduler from './hooks/useBackgroundScheduler';

/**
 * ProtectedRoute — only renders children if user is authenticated and authorised.
 * Redirects to login if not.
 */
function ProtectedRoute({ children }) {
  const {
    isAuthenticated,
    isAuthorised,
    isLoading,
    isCheckingIntroGate,
    needsRoleChoice,
    needsIntroVideo,
  } = useAuth();

  // While selectRole is awaiting the SAST-date gate read, render the loading
  // screen instead of the dashboard so it cannot flash before the video
  // overlay mounts.
  if (isLoading || isCheckingIntroGate) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated || !isAuthorised) {
    return <Navigate to="/login" replace />;
  }

  if (needsRoleChoice) {
    return <RoleChooser />;
  }

  // Redirect to intro video if flagged by a fresh OAuth login
  if (needsIntroVideo) {
    return <Navigate to="/intro" replace />;
  }

  return children;
}

/**
 * LoginRoute — renders the login page for unauthenticated users.
 * Redirects authenticated+authorised users to the dashboard.
 */
function LoginRoute() {
  const { isAuthenticated, isAuthorised, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated && isAuthorised) {
    return <Navigate to="/" replace />;
  }

  return <Login />;
}

/**
 * IntroGuard — protects the /intro route from direct URL access.
 * Only allows rendering if needsIntroVideo is true (set by fresh OAuth login).
 * Otherwise redirects to the dashboard.
 */
function IntroGuard({ children }) {
  const { needsIntroVideo, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (!needsIntroVideo) return <Navigate to="/" replace />;
  return children;
}

/**
 * DashboardRouter — renders the correct dashboard based on user role.
 * Adviser (Marinus) sees the pipeline overview.
 * Administration Assistant (Wouter) sees the inbox + active workspace.
 * New Business Assistant (Vicky) sees the shared pipeline with the
 * interactive prep-request working view.
 */
function DashboardRouter() {
  const { role } = useAuth();

  // Activate calendar polling for adviser (5-minute interval)
  useCalendarPolling(role === 'adviser');

  // Activate background scheduler for SLA checks and weekly reports
  useBackgroundScheduler(role === 'adviser');

  if (role === 'adviser') return <AdviserDashboard />;
  if (role === 'assistant') return <AssistantDashboard />;
  if (role === 'new_business_assistant') return <NewBusinessDashboard />;

  // Fallback — should not happen with whitelist, but handle gracefully
  return <AdviserDashboard />;
}

/**
 * AdviserOnlyRoute — restricts access to adviser-only views (e.g. Settings).
 */
function AdviserOnlyRoute({ children }) {
  const { role } = useAuth();
  if (role !== 'adviser') return <Navigate to="/" replace />;
  return children;
}

/**
 * AdviserOrAssistantRoute — views shared by the Adviser and the
 * Administration Assistant (currently the prep-request submission form,
 * CLAUDE.md 26.1: both roles may load new preparation requests; the New
 * Business Assistant never loads them — she works them).
 */
function AdviserOrAssistantRoute({ children }) {
  const { role } = useAuth();
  if (role !== 'adviser' && role !== 'assistant') return <Navigate to="/" replace />;
  return children;
}

/**
 * Loading screen shown while auth state is being determined.
 */
function LoadingScreen() {
  return (
    <div style={loadingStyles.container}>
      <div style={loadingStyles.logo}>
        <svg width="40" height="40" viewBox="0 0 110 110" xmlns="http://www.w3.org/2000/svg">
          <rect x="0" y="0" width="110" height="110" rx="22" fill="#0F4C75"/>
          <rect x="12" y="62" width="86" height="38" rx="6" fill="#1B9AAA" opacity="0.5"/>
          <rect x="12" y="82" width="86" height="18" rx="5" fill="#1B9AAA" opacity="0.4"/>
          <rect x="42" y="89" width="26" height="7" rx="3.5" fill="#F4A261"/>
          <rect x="24" y="18" width="62" height="72" rx="5" fill="white"/>
          <polygon points="70,18 86,18 86,34" fill="#0F4C75" opacity="0.2"/>
          <rect x="32" y="34" width="26" height="5" rx="2.5" fill="#F4A261"/>
          <rect x="32" y="46" width="38" height="3" rx="1.5" fill="#0F4C75" opacity="0.3"/>
          <rect x="32" y="55" width="32" height="3" rx="1.5" fill="#0F4C75" opacity="0.3"/>
          <rect x="32" y="64" width="28" height="3" rx="1.5" fill="#0F4C75" opacity="0.3"/>
        </svg>
        <span style={loadingStyles.text}>Inbox</span>
      </div>
      <div style={loadingStyles.spinner} />
    </div>
  );
}

const loadingStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#F0F4F8',
    gap: '1.5rem',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  text: {
    fontSize: '1.4rem',
    fontWeight: 700,
    color: '#0F4C75',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #E8EFF5',
    borderTop: '3px solid var(--cp-secondary)',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
};

// Add spinner animation via style tag
const spinnerStyle = document.createElement('style');
spinnerStyle.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
document.head.appendChild(spinnerStyle);

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Login page — redirects to dashboard if already authenticated */}
          <Route path="/login" element={<LoginRoute />} />

          {/* Intro video — only accessible after a fresh OAuth login */}
          <Route
            path="/intro"
            element={
              <IntroGuard>
                <IntroVideo />
              </IntroGuard>
            }
          />

          {/* Protected app routes — wrapped in AppShell layout */}
          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<DashboardRouter />} />
            <Route path="/submit" element={<AdviserOnlyRoute><SubmitFile /></AdviserOnlyRoute>} />
            <Route path="/submit-prep" element={<AdviserOrAssistantRoute><SubmitPrepRequest /></AdviserOrAssistantRoute>} />
            <Route path="/file/:id" element={<FileDetail />} />
            <Route path="/prep/:id" element={<PrepRequestDetail />} />
            {/* Referral page — Adviser only (referral cards are inert for the other roles). */}
            <Route path="/referral/:id" element={<AdviserOnlyRoute><ReferralDetail /></AdviserOnlyRoute>} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/tools" element={<Tools />} />
            <Route path="/reviews" element={<AdviserOnlyRoute><Reviews /></AdviserOnlyRoute>} />
            <Route path="/achievements" element={<Achievements />} />
            <Route path="/settings" element={<AdviserOnlyRoute><Settings /></AdviserOnlyRoute>} />
            <Route path="/settings/:section" element={<AdviserOnlyRoute><Settings /></AdviserOnlyRoute>} />
          </Route>

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
